import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import {
  fractionFromAmountCents,
  fractionToAmountCents,
} from '@utils/allocationUnits';
import { unlockPatch } from './resolveCellEdit';

export const CellStates = {
  LOCKED: 'LOCKED',
  UPLOADED: 'UPLOADED',
  DERIVED: 'DERIVED',
} as const;
export type CellState = (typeof CellStates)[keyof typeof CellStates];

type CellLockMaps = {
  lockedCells: Map<string, number>;
  lockedAmountCents: Map<string, number>;
  uploadedCells: Set<string>;
};

type UploadedCellValue = {
  key: string;
  fraction: number;
  amountCents?: number;
};

export type CellUploadInput = {
  subAccountId: string;
  financialProductId: string;
  fraction: number;
};

// Rounded through whole cents, the same way a typed lock is stored, so an
// uploaded cell shows exactly the value that was locked. A $0 account keeps
// the fraction, as a typed lock does.
export const toUploadedCellValues = (
  cells: CellUploadInput[],
  mvCentsOf: (subAccountId: string) => number,
) =>
  cells.map(({ subAccountId, financialProductId, fraction }) => {
    const key = allocationCellKey(subAccountId, financialProductId);
    const mv = mvCentsOf(subAccountId);
    if (mv <= 0) return { key, mv, fraction, amountCents: undefined };

    const amountCents = fractionToAmountCents(fraction, mv);
    return {
      key,
      mv,
      fraction: fractionFromAmountCents(amountCents, mv),
      amountCents,
    };
  });

export const cellStateOf = (
  key: string,
  {
    lockedCells,
    uploadedCells,
  }: Pick<CellLockMaps, 'lockedCells' | 'uploadedCells'>,
) => {
  if (uploadedCells.has(key)) return CellStates.UPLOADED;
  if (lockedCells.has(key)) return CellStates.LOCKED;
  return CellStates.DERIVED;
};

export const resolveCellRelease = (input: CellLockMaps & { key: string }) => {
  if (!input.lockedCells.has(input.key)) return null;
  return unlockPatch(input);
};

// Uploads lock unconditionally, even at the starting value: uploaded cells must
// hold on Recalculate rather than fall back to DERIVED and be re-solved.
export const resolveCellUpload = (
  cells: UploadedCellValue[],
  { lockedCells, lockedAmountCents, uploadedCells }: CellLockMaps,
) => {
  if (cells.length === 0) return null;

  const nextLocks = new Map(lockedCells);
  const nextLockCents = new Map(lockedAmountCents);
  const nextUploaded = new Set(uploadedCells);
  for (const { key, fraction, amountCents } of cells) {
    nextLocks.set(key, fraction);
    if (amountCents === undefined) {
      nextLockCents.delete(key);
    } else {
      nextLockCents.set(key, amountCents);
    }
    nextUploaded.add(key);
  }

  return {
    lockedCells: nextLocks,
    lockedAmountCents: nextLockCents,
    uploadedCells: nextUploaded,
    draft: new Map<string, number>(),
    solveStatus: null,
  };
};
